import { transformFlowToStages } from '@/features/boe-task/-query/transformers';
import type { ApprovalTaskConfigFlow } from '@/features/boe-task/components/ApprovalTaskConfig/ApprovalTaskConfig';
import { adjustFlowToStageCount } from '@/features/boe-task/components/ApprovalTaskConfig/utils/flowGenerator';
import type { FreeFormQuestionJson } from '@/features/boe-task/types/free-form';
import { mapConfigQuestionsToJson, mapJsonQuestionsToConfigQuestions } from '@/features/boe-task/utils/free-form-mappers';
import { DEFAULT_IN_HOUSE_PACKAGE_DISPLAY_NAME } from '../constants';
import type { PackagePatternDraftConfig, PackagePatternDraftStatus, PackagePatternDraftTodoConfig, PatternDraftQuestion } from '../types/pattern-draft-config';
import type { DraftAssignRuleJson } from './draft-assign-rules';
export type { FreeFormQuestionJson } from '@/features/boe-task/types/free-form';
export type { DraftAssignRuleJson } from './draft-assign-rules';

/**
 * BOE パッケージ下書きの editingContent（PatternSetting 相当の宣言形 JSON）。
 *
 * NOTE: schema.d.ts では editingContent が `Record<string, never>`（型生成されない）ため、
 * backend の DraftPatternSettingJson（draft-pattern-setting-codec）に合わせて frontend で型を定義する。
 * frontend は freeForm.questions と approvalTask（段階・担当グループ）を編集する。
 * Flow / Task Entity ID は publish 後に生成されるが、下書き中の承認段階は approvalStages 拡張フィールドに保持する。
 */
export type DraftPatternSettingJson = {
  name: string;
  description?: string;
  anchorDateLabel: string;
  anchorDateLabelCode?: string;
  operationPackageType: string;
  allowIndividualRequest: boolean;
  steps?: DraftStepJson[];
};

/** 未編集フィールド（assignRules 等）は保持のため緩く型付けする。 */
type DraftStepJson = {
  /**
   * ステップ名（BE の StepSetting.name）。下書き画面ではステップ見出しとして表示・編集する。
   * 過去に作られた editingContent では欠落しうるため optional で読む。
   */
  name?: string;
  /**
   * ステップの説明（BE の StepSetting.description）。名称と同じくステップ見出しで編集する。
   * 未設定の editingContent があるため optional で読み、空文字は書かずにキーを落とす。
   */
  description?: string;
  nodes?: DraftNodeJson[];
  [key: string]: unknown;
};

/** 下書き editingContent 内の承認段階（FE 拡張。publish 後は approval-flow API へ反映）。 */
export type DraftApprovalStageJson = {
  stageIndex: number;
  peopleGroupId: string;
  peopleGroupName?: string;
  requiredApprovals?: number;
  designationMode?: 'none' | 'optional' | 'required';
  /**
   * 段の表示名。publish 時に BE はカタログ既定名で段を作るため、
   * ここに保持した値を公開後の追いPUT（savePublishedApprovalFlow）で反映する。
   */
  name?: string;
};
type DraftApprovalTaskJson = {
  requiredApprovals: number;
  operationApprovalTaskCatalogCode: string;
  /**
   * node レベルの指名モード（BE の publish コーデックが読み取り
   * `operation_engine_node.designation_mode` に確定させる）。DC-8217 A案。
   */
  designationMode?: 'none' | 'optional' | 'required';
  approvalStages?: DraftApprovalStageJson[];
  /**
   * 差し戻し可否。**optional**（旧 draft はキー自体を持たない）。欠落は false として扱う。
   * publish 時に BE の draft codec が `oe_approval_task.allow_send_back` に確定させる。
   */
  allowSendBack?: boolean;
  [key: string]: unknown;
};
type DraftTodoTaskJson = {
  displayName: string;
  description?: string;
  checklist: {
    id: string;
    label: string;
    order: number;
  }[];
  operationTodoTaskCatalogCode?: string;
  [key: string]: unknown;
};
type DraftNodeJson = {
  assignRules?: DraftAssignRuleJson[];
  submitTask?: {
    /**
     * 提出タスクの表示名。BE が optional フィールドとして追加したもので、
     * 未設定のときは BE が resolve-on-read で返す表示名（taskDisplayNames）を使う。
     */
    name?: string;
    freeForm?: {
      name: string;
      description: string | null;
      questions: FreeFormQuestionJson[];
    };
    /**
     * 引き戻し可否。**optional**（旧 draft はキー自体を持たない）。欠落は false として扱う。
     * publish 時に BE の draft codec が `oe_submit_task.allow_recall` に確定させる。
     */
    allowRecall?: boolean;
    [key: string]: unknown;
  };
  approvalTask?: DraftApprovalTaskJson;
  todoTask?: DraftTodoTaskJson;
  [key: string]: unknown;
};
const DEFAULT_APPROVAL_FLOW: ApprovalTaskConfigFlow = {
  id: 'start-default',
  type: 'start',
  children: [{
    id: 'end-default',
    type: 'end'
  }]
};
export function hasApprovalTaskInDraft(content: DraftPatternSettingJson): boolean {
  return findApprovalTaskNode(content) != null;
}
export function hasTodoTaskInDraft(content: DraftPatternSettingJson): boolean {
  return findTodoTaskNode(content) != null;
}
function findApprovalTaskNode(content: DraftPatternSettingJson): DraftNodeJson | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.approvalTask) {
        return node;
      }
    }
  }
  return undefined;
}
function findTodoTaskNode(content: DraftPatternSettingJson): DraftNodeJson | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.todoTask) {
        return node;
      }
    }
  }
  return undefined;
}

/** editingContent の todoTask / assignRules から下書き TODO 編集 state を組み立てる。 */
export function toDraftTodoConfig(content: DraftPatternSettingJson): PackagePatternDraftTodoConfig | null {
  const node = findTodoTaskNode(content);
  if (!node?.todoTask) return null;
  return {
    displayName: node.todoTask.displayName,
    description: node.todoTask.description ?? '',
    checklist: node.todoTask.checklist ?? [],
    assignRules: node.assignRules ?? []
  };
}

/** TODO 編集結果を editingContent の todo ノードへ反映する。 */
function applyTodoDraftToEditingContent(args: {
  base: DraftPatternSettingJson;
  todo: PackagePatternDraftTodoConfig;
}): DraftPatternSettingJson {
  const next = structuredClone(args.base);
  const node = findTodoTaskNode(next);
  if (!node?.todoTask) return next;
  node.todoTask.displayName = args.todo.displayName;
  node.todoTask.description = args.todo.description || undefined;
  node.todoTask.checklist = args.todo.checklist;
  node.assignRules = args.todo.assignRules;
  return next;
}

/** 公開前チェックで見つかった TODO の不備。 */
export type TodoDraftError = 'assignee' | 'checklist';

/**
 * 公開前に TODO が満たすべき条件を検査する。BE の publish ガードと同じ条件。
 *
 * - `assignee`: 担当者（assignRules）が 1 件も無い（DC-9068）
 * - `checklist`: チェックリストが 0 件、または id / ラベルが空の項目がある（DC-9286）
 *
 * チェックリストの項目に id が無いまま公開すると、インスタンス側に完了操作できない項目が生まれ、
 * TODO タスクを完了できなくなるため、件数だけでなく中身も見る。
 */
export function findTodoDraftError(todo: PackagePatternDraftTodoConfig | null | undefined): TodoDraftError | null {
  if ((todo?.assignRules?.length ?? 0) < 1) return 'assignee';
  const checklist = todo?.checklist ?? [];
  if (checklist.length < 1) return 'checklist';
  const hasBrokenItem = checklist.some(item => !item.id?.trim() || !item.label?.trim());
  return hasBrokenItem ? 'checklist' : null;
}
function collectPeopleGroupNodes(flow: ApprovalTaskConfigFlow): Extract<ApprovalTaskConfigFlow, {
  type: 'peopleGroup';
}>[] {
  const nodes: Extract<ApprovalTaskConfigFlow, {
    type: 'peopleGroup';
  }>[] = [];
  function walk(node: ApprovalTaskConfigFlow): void {
    if (node.type === 'peopleGroup') {
      nodes.push(node);
    }
    for (const child of node.children ?? []) {
      walk(child);
    }
  }
  walk(flow);
  return nodes;
}

/** editingContent の approvalTask / approvalStages から ApprovalTaskConfigFlow を組み立てる。 */
export function toApprovalDraftFlow(content: DraftPatternSettingJson): ApprovalTaskConfigFlow | null {
  const approvalTask = findApprovalTaskNode(content)?.approvalTask;
  if (!approvalTask) return null;
  const stages = approvalTask.approvalStages ?? [];
  if (stages.length === 0) {
    return adjustFlowToStageCount(DEFAULT_APPROVAL_FLOW, 1);
  }
  const sorted = [...stages].sort((a, b) => a.stageIndex - b.stageIndex);
  const approverNodes: ApprovalTaskConfigFlow[] = sorted.map((stage, index) => ({
    id: `approver-${index + 1}`,
    type: 'peopleGroup',
    peopleGroup: stage.peopleGroupId ? {
      id: stage.peopleGroupId,
      name: stage.peopleGroupName ?? ''
    } : undefined,
    designationMode: stage.designationMode,
    // 下書きでは FE 保持値が正なので編集値（name）として復元する。
    // 公開後の追いPUT に載る必要があるため savedName には入れない。
    name: stage.name
  }));
  for (let index = 0; index < approverNodes.length; index++) {
    const current = approverNodes[index];
    if (!current) continue;
    const next = approverNodes[index + 1];
    current.children = next ? [next] : [{
      id: 'end',
      type: 'end'
    }];
  }
  const firstApprover = approverNodes[0];
  return {
    id: 'start',
    type: 'start',
    children: firstApprover !== undefined ? [firstApprover] : [{
      id: 'end',
      type: 'end'
    }]
  };
}

/**
 * editingContent から差し戻し / 引き戻しの可否を読む。
 *
 * 承認ノード / 申請ノードに分かれて載っているため 1 か所に集約する。
 * どちらのキーも旧 draft には存在しないので、欠落は false（= 従来の挙動）として扱う。
 */
export function toApprovalReturnPolicyDraft(content: DraftPatternSettingJson): {
  allowSendBack: boolean;
  allowRecall: boolean;
} {
  const approvalTask = findApprovalTaskNode(content)?.approvalTask;
  const submitTask = findSubmitTask(content);
  return {
    allowSendBack: approvalTask?.allowSendBack === true,
    allowRecall: submitTask?.allowRecall === true
  };
}

/**
 * 差し戻し / 引き戻しの可否を editingContent へ反映する。
 *
 * 保存は下書き保存 / 公開の全体 PUT に相乗りする（利用中パターンの
 * `PUT approval-flow` とは別経路。詳細は ApprovalTaskConfig の onReturnPolicyChange）。
 */
export function applyApprovalReturnPolicyToEditingContent(args: {
  base: DraftPatternSettingJson;
  policy: {
    allowSendBack: boolean;
    allowRecall: boolean;
  };
}): DraftPatternSettingJson {
  const next = structuredClone(args.base);
  const approvalTask = findApprovalTaskNode(next)?.approvalTask;
  if (approvalTask) {
    approvalTask.allowSendBack = args.policy.allowSendBack;
  }
  const submitTask = findSubmitTask(next);
  if (submitTask) {
    submitTask.allowRecall = args.policy.allowRecall;
  }
  return next;
}

/** 承認フロー編集結果を editingContent の approvalStages へ反映する。 */
export function applyApprovalDraftToEditingContent(args: {
  base: DraftPatternSettingJson;
  flow: ApprovalTaskConfigFlow;
}): DraftPatternSettingJson {
  const next = structuredClone(args.base);
  const approvalTask = findApprovalTaskNode(next)?.approvalTask;
  if (!approvalTask) return next;
  const peopleGroupNodes = collectPeopleGroupNodes(args.flow);
  const stages = transformFlowToStages(args.flow);
  approvalTask.approvalStages = stages.map((stage, index) => {
    const node = peopleGroupNodes[index];
    return {
      stageIndex: stage.stageIndex,
      peopleGroupId: stage.peopleGroupId,
      peopleGroupName: node?.peopleGroup?.name,
      requiredApprovals: stage.requiredApprovals,
      designationMode: stage.designationMode,
      name: stage.name
    };
  });

  // node レベルの単一値（BE publish 用 / DC-8217 A案）。現行エディタの承認は publish 時に
  // 単一の承認ノードへ写るため、代表値として先頭段の designationMode を採用する。
  // 多段で段ごとに異なる designationMode は node 単一値では表現しきれず、引き続き
  // 公開後の追いPUT（savePublishedApprovalFlow）経由で反映される。
  approvalTask.designationMode = stages[0]?.designationMode ?? 'none';
  return next;
}

/** 公開前に承認段階の担当グループがすべて設定されているか。 */
export function isApprovalDraftFlowValid(flow: ApprovalTaskConfigFlow): boolean {
  const peopleGroupNodes = collectPeopleGroupNodes(flow);
  if (peopleGroupNodes.length === 0) return false;
  return peopleGroupNodes.every(node => node.peopleGroup?.id && node.peopleGroup.id.length > 0);
}

/**
 * BE の status（Published / Draft / Archived / Editing）を frontend の状態へ写す。
 * Draft → draft、Archived → archived、それ以外（Published / Editing）→ published。
 * 以前は Draft 以外を一律 published に潰していたが、archived タブ表示のため撤廃した。
 */
function toPatternDraftStatus(status: string): PackagePatternDraftStatus {
  switch (status) {
    case 'Draft':
      return 'draft';
    case 'Archived':
      return 'archived';
    default:
      return 'published';
  }
}
function getFreeForm(content: DraftPatternSettingJson) {
  return findSubmitTaskWithFreeForm(content)?.freeForm;
}

/**
 * 申請ノードの submitTask を返す（freeForm を持たない申請にも使える）。
 * 引き戻し可否は freeForm の有無に関係なく載るため、フォーム前提の
 * {@link findSubmitTaskWithFreeForm} とは別に用意する。
 */
function findSubmitTask(content: DraftPatternSettingJson): DraftNodeJson['submitTask'] | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.submitTask) {
        return node.submitTask;
      }
    }
  }
  return undefined;
}
function findSubmitTaskWithFreeForm(content: DraftPatternSettingJson): DraftNodeJson['submitTask'] | undefined {
  for (const step of content.steps ?? []) {
    for (const node of step.nodes ?? []) {
      if (node.submitTask?.freeForm) {
        return node.submitTask;
      }
    }
  }
  return undefined;
}

/** editingContent の freeForm.questions を PatternDraftQuestion[] へ変換する。 */
function toPatternDraftQuestions(content: DraftPatternSettingJson): PatternDraftQuestion[] {
  const freeForm = getFreeForm(content);
  if (!freeForm) return [];
  return mapJsonQuestionsToConfigQuestions(freeForm.questions);
}

/**
 * editingContent の submitTask.name を読む。
 *
 * BE が optional で追加したフィールドのため、キー欠落・空文字はいずれも「未設定」として
 * null を返す（呼び出し側は BE 解決の表示名にフォールバックする）。
 */
function toDraftSubmitTaskName(content: DraftPatternSettingJson): string | null {
  return findSubmitTaskWithFreeForm(content)?.name?.trim() || null;
}

/** getDraft のレスポンスから下書きエディタの初期 config を組み立てる。 */
export function toPackagePatternDraftConfig(args: {
  patternId: string;
  editingContent: DraftPatternSettingJson;
  status: string;
  packageDisplayName?: string;
}): PackagePatternDraftConfig {
  const {
    patternId,
    editingContent,
    status,
    packageDisplayName
  } = args;
  return {
    operationPackagePatternId: patternId,
    packageName: packageDisplayName ?? DEFAULT_IN_HOUSE_PACKAGE_DISPLAY_NAME,
    name: editingContent.name,
    description: editingContent.description ?? '',
    status: toPatternDraftStatus(status),
    createdBy: null,
    updatedBy: null,
    updatedAt: null,
    questions: toPatternDraftQuestions(editingContent),
    stepNames: (editingContent.steps ?? []).map(step => step.name ?? ''),
    stepDescriptions: (editingContent.steps ?? []).map(step => step.description ?? ''),
    submitTaskName: toDraftSubmitTaskName(editingContent)
  };
}

/**
 * ステップ見出しの編集結果を steps[].name / steps[].description へ書き戻す。
 * stepNames / stepDescriptions は editingContent の steps と同じ順序で並ぶため
 * index で対応させ、ステップ自体の増減は行わない。
 *
 * 説明の空文字は「説明なし」を意味するため、キーを立てずに undefined へ落とす
 * （PUT の JSON からキーごと消える）。
 */
function applyStepHeadings(content: DraftPatternSettingJson, stepNames: string[], stepDescriptions: string[]): void {
  content.steps?.forEach((step, index) => {
    const name = stepNames[index];
    if (name !== undefined) {
      step.name = name;
    }
    const description = stepDescriptions[index];
    if (description !== undefined) {
      step.description = description || undefined;
    }
  });
}

/**
 * 下書きエディタの編集結果を editingContent へ反映する（全体 PUT 用）。
 */
export function applyPackagePatternDraftToEditingContent(args: {
  base: DraftPatternSettingJson;
  config: Pick<PackagePatternDraftConfig, 'name' | 'description' | 'questions' | 'stepNames' | 'stepDescriptions' | 'submitTaskName'>;
  approvalFlow?: ApprovalTaskConfigFlow;
  /** 差し戻し / 引き戻しの可否（下書きエディタが保持している値） */
  returnPolicy?: {
    allowSendBack: boolean;
    allowRecall: boolean;
  };
  todo?: PackagePatternDraftTodoConfig | null;
}): DraftPatternSettingJson {
  const {
    base,
    config,
    approvalFlow,
    returnPolicy,
    todo
  } = args;
  let next = structuredClone(base);
  next.name = config.name;
  next.description = config.description;
  applyStepHeadings(next, config.stepNames, config.stepDescriptions);
  const submitTask = findSubmitTaskWithFreeForm(next);
  if (submitTask?.freeForm) {
    submitTask.freeForm.questions = mapConfigQuestionsToJson(config.questions);
  }
  // 未設定（null）のままキーを作ると BE 解決の表示名へのフォールバックが効かなくなるため、
  // 名称が入っているときだけ書き込む。
  if (submitTask && config.submitTaskName !== null) {
    submitTask.name = config.submitTaskName;
  }
  if (approvalFlow && hasApprovalTaskInDraft(next)) {
    next = applyApprovalDraftToEditingContent({
      base: next,
      flow: approvalFlow
    });
  }
  if (returnPolicy) {
    next = applyApprovalReturnPolicyToEditingContent({
      base: next,
      policy: returnPolicy
    });
  }
  if (todo && hasTodoTaskInDraft(next)) {
    next = applyTodoDraftToEditingContent({
      base: next,
      todo
    });
  }
  return next;
}